import { useMemo, useState } from "react";
import { FaRegEdit, FaTrashAlt, FaWarehouse } from "react-icons/fa";
import { IoAdd } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import LabPopup from "../components/labs/LabPopup";
import LoadingOverlay from "../components/Loading";
import StorageLocationPopup from "../components/StorageLocationPopup";
import { useConfirm } from "../confirm/ConfirmProvider";
import { useUser } from "../contexts/UserContext";
import { getAlertsCount } from "../fetch/AlertRequest";
import { deleteLab } from "../fetch/labRequest";
import { saveLastLab } from "../function/lastLab";
import styles from "../styles/modules/LabListPage.module.css";
import { Lab } from "../types";

function LabListPage() {
  const {
    user,
    labs,
    setLabs,
    userLabs,
    setUserLabs,
    memberships,
    isLoading,
    setAlertCount,
  } = useUser();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [storageLab, setStorageLab] = useState<Lab | null>(null);
  const [showLabPopup, setShowLabPopup] = useState(false);
  const [targetLab, setTargetLab] = useState<Lab | null>(null);
  const [busy, setBusy] = useState(false);

  const isTenantAdmin = useMemo(
    () => memberships.some((m) => m.role_code === "tenant_admin"),
    [memberships]
  );

  // 大学管理者はテナント全研究室を、それ以外は所属研究室のみを表示する。
  // 表示順は登録順（id 昇順）にする。
  const displayedLabs = sortById(isTenantAdmin ? labs : userLabs);

  function isLabAdminOf(labId: number): boolean {
    return memberships.some(
      (m) => m.role_code === "lab_admin" && m.lab_id === labId
    );
  }

  // その研究室に所属しているか。大学管理者でも未所属の研究室があり得るため、
  // 未所属の研究室へは進入させない（誤って別の研究室に入るのを防ぐ）。
  function isMember(labId: number): boolean {
    return userLabs.some((l) => l.id === labId);
  }

  // 編集は大学管理者、または対象研究室の管理者に許可する。
  function canEditLab(labId: number): boolean {
    return isTenantAdmin || isLabAdminOf(labId);
  }

  function selectLab(labId: number) {
    // ホームボタンから直接この研究室へ戻れるよう、最後に選んだ研究室を保存する。
    if (user) saveLastLab(user.id, labId);
    navigate("/reagents", { state: { labId } });
  }

  function openLabPopup(lab: Lab | null) {
    setTargetLab(lab);
    setShowLabPopup(true);
  }

  // 登録順（id 昇順）に並べる。
  function sortById(list: Lab[]): Lab[] {
    return [...list].sort((a, b) => a.id - b.id);
  }

  function upsert(list: Lab[], lab: Lab): Lab[] {
    const map = new Map(list.map((l) => [l.id, l]));
    map.set(lab.id, lab);
    return sortById(Array.from(map.values()));
  }

  // 追加・編集後の反映。全体一覧は upsert、所属一覧は既存分のみ更新する。
  function updateLabList(updatedLab: Lab) {
    setLabs(upsert(labs, updatedLab));
    if (userLabs.some((l) => l.id === updatedLab.id)) {
      setUserLabs(upsert(userLabs, updatedLab));
    }
  }

  async function handleDeleteLab(lab_id: number) {
    const labName = labs.find((l) => l.id === lab_id)?.name ?? "";
    if (
      !(await confirm(
        `「${labName}」を削除します。\nこの研究室内のすべての試薬マスター・試薬・保管場所・権限が完全に削除され、元に戻せません。`,
        {
          danger: true,
          confirmText: "削除",
          requireText: labName,
        },
      ))
    ) {
      return;
    }

    setBusy(true);
    try {
      const success = await deleteLab(lab_id);
      if (success) {
        const nextLabs = labs.filter((l) => l.id !== lab_id);
        const nextUserLabs = userLabs.filter((l) => l.id !== lab_id);
        setLabs(nextLabs);
        setUserLabs(nextUserLabs);

        if (user?.tenant_id) {
          const lab_ids = nextUserLabs.map((l) => l.id);
          const alertCount = await getAlertsCount({
            tenant_id: user.tenant_id,
            lab_ids,
          });
          setAlertCount(alertCount);
        }
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className={styles["lab-list-page"]}>
        <h2>研究室を選択</h2>

        {!isLoading && displayedLabs.length === 0 && (
          <p className={styles["empty"]}>
            {isTenantAdmin
              ? "研究室がありません。下の＋ボタンから登録してください。"
              : "所属している研究室がありません。管理者にお問い合わせください。"}
          </p>
        )}

        <div className={styles["lab-grid"]}>
          {displayedLabs.map((lab) => {
            const canEnter = isMember(lab.id);
            return (
              <div
                key={lab.id}
                className={`${styles["lab-card"]} ${
                  canEnter ? "" : styles["lab-card-disabled"]
                }`}
                onClick={() => {
                  if (canEnter) selectLab(lab.id);
                }}
                role={canEnter ? "button" : undefined}
                aria-disabled={!canEnter}
              >
                <div className={styles["lab-actions"]}>
                  {canEditLab(lab.id) && (
                    <button
                      className={styles["lab-action"]}
                      onClick={(e) => {
                        e.stopPropagation();
                        openLabPopup(lab);
                      }}
                      title="研究室を編集"
                      aria-label="研究室を編集"
                    >
                      <FaRegEdit />
                    </button>
                  )}
                  {isTenantAdmin && (
                    <button
                      className={`${styles["lab-action"]} ${styles["lab-action-delete"]}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteLab(lab.id);
                      }}
                      title="研究室を削除"
                      aria-label="研究室を削除"
                    >
                      <FaTrashAlt />
                    </button>
                  )}
                  {isLabAdminOf(lab.id) && (
                    <button
                      className={styles["lab-action"]}
                      onClick={(e) => {
                        e.stopPropagation();
                        setStorageLab(lab);
                      }}
                      title="保管場所を管理"
                      aria-label="保管場所を管理"
                    >
                      <FaWarehouse />
                    </button>
                  )}
                </div>
                <span className={styles["lab-name"]}>{lab.name}</span>
                {!canEnter && (
                  <span className={styles["lab-note"]}>未所属</span>
                )}
              </div>
            );
          })}
        </div>

        {isTenantAdmin && (
          <div className={styles["fab-row"]}>
            <button
              className={styles["add-fab"]}
              onClick={() => openLabPopup(null)}
              title="研究室を追加"
              aria-label="研究室を追加"
            >
              <IoAdd />
            </button>
          </div>
        )}
      </div>

      {storageLab && (
        <StorageLocationPopup
          lab={storageLab}
          onClose={() => setStorageLab(null)}
        />
      )}

      {showLabPopup && (
        <LabPopup
          targetLab={targetLab}
          onClose={() => setShowLabPopup(false)}
          onLoading={setBusy}
          updateLabList={updateLabList}
        />
      )}

      {(isLoading || busy) && <LoadingOverlay />}
    </>
  );
}

export default LabListPage;
